import React from 'react';

import { Bookmark, Flag } from 'lucide-react';

import CheckboxField from '../common/formElements/CheckboxField';

// The member's two states for a club: played, and on the bucket list (D1/D2 in
// docs/EXPLORE.md). States, not actions — so they are checkboxes the member sets, never
// call-to-action buttons.
//
// Shared by the detail drawer and the course card so the two cannot drift. CheckboxField is the
// app's shared control, so these also match every other checkbox in the product; it renders the
// control only — no label — so each row supplies its own icon and text and puts the control on the
// right.
//
// The click target is the label and the control, not the whole row. Putting an onClick on the row
// as well looks like an easy win and is not: a click on the label is forwarded to the input and
// bubbles back out, so the row handler fires twice and the state toggles straight back off.
//
// Both states are saved to the member's account by useMyCourseFlags. Explore gates both behind the
// member login (D7), so a signed-out visitor gets the member-setup drawer here rather than a tick.
interface CourseStateTogglesProps {
  // Ids have to be unique per rendered instance, not per component: the list view puts dozens of
  // these on one page, and a repeated id makes every label point at the first card's control.
  // The course id is the only thing to hand that is stable and unique.
  idPrefix: string;
  isPlayed: boolean;
  isBucketListed: boolean;
  onTogglePlayed: () => void;
  onBucketList: () => void;
  className?: string;
}

const CourseStateToggles: React.FC<CourseStateTogglesProps> = ({
  idPrefix,
  isPlayed,
  isBucketListed,
  onTogglePlayed,
  onBucketList,
  className = '',
}) => {
  const playedId = `${idPrefix}-played`;
  const bucketListId = `${idPrefix}-bucket-list`;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
        <Flag
          className={`h-4 w-4 shrink-0 ${isPlayed ? 'text-primary' : 'text-white/30'}`}
        />
        <label
          htmlFor={playedId}
          className="flex-1 cursor-pointer text-sm font-semibold text-white/80"
        >
          I have played this course
        </label>
        {/* haptic: this is the tick the buzz was asked for. See IOS_SWITCH_PROPS for why it is a
            property of the control rather than something the handler can ask for. */}
        <CheckboxField
          id={playedId}
          value={isPlayed}
          onChange={onTogglePlayed}
          haptic
        />
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
        <Bookmark
          className={`h-4 w-4 shrink-0 ${isBucketListed ? 'text-primary' : 'text-white/30'}`}
        />
        <label
          htmlFor={bucketListId}
          className="flex-1 cursor-pointer text-sm font-semibold text-white/80"
        >
          Add to my bucket list
        </label>
        {/* haptic: same buzz as played above. Both states are saved the same way, so they should
            feel the same under the finger. */}
        <CheckboxField
          id={bucketListId}
          value={isBucketListed}
          onChange={onBucketList}
          haptic
        />
      </div>
    </div>
  );
};

export default CourseStateToggles;
